import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About Your Doctor
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Dedicated to providing exceptional healthcare with compassion, expertise, and a patient-first approach
            </p>
          </div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 items-start">
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                <img 
                  src="https://public.readdy.ai/ai/img_res/20badbfdfdb23fab3ad65a62b15b1c21.jpg"
                  alt="Doctor Professional Portrait"
                  className="w-full h-auto rounded-2xl shadow-2xl mb-8 object-cover object-top"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-graduation-cap-line', label: 'Board Certified', sublabel: 'Family Medicine' },
                    { icon: 'ri-award-line', label: 'Excellence Award', sublabel: '2023 Recipient' },
                    { icon: 'ri-user-heart-line', label: '5000+ Patients', sublabel: 'Served' },
                    { icon: 'ri-global-line', label: '3 Languages', sublabel: 'English, French, Spanish' }
                  ].map((badge, index) => (
                    <div key={index} className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 text-center border border-blue-100">
                      <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-3">
                        <i className={`${badge.icon} text-3xl text-blue-600`}></i>
                      </div>
                      <p className="font-bold text-gray-900 mb-1">{badge.label}</p>
                      <p className="text-xs text-gray-600">{badge.sublabel}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="prose prose-lg max-w-none">
                <h2 className="font-serif text-4xl font-bold text-gray-900 mb-6">
                  Dr. Sarah Johnson, MD, CCFP
                </h2>
                
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-8">
                  <p className="text-lg text-gray-700 italic">
                    "My mission is to provide personalized, compassionate care that empowers patients to achieve their best health and well-being."
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">Professional Background</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With over 15 years of experience in family medicine, I have dedicated my career to providing comprehensive, patient-centered healthcare to individuals and families across Canada. I completed my medical degree at the University of Toronto Faculty of Medicine, followed by a residency in Family Medicine at St. Michael's Hospital.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Throughout my career, I have maintained a strong commitment to continuing medical education, staying current with the latest advances in medical research and treatment protocols. I am board-certified by the College of Family Physicians of Canada and hold active membership in several professional medical associations.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">Education & Credentials</h3>
                <ul className="space-y-3 mb-8">
                  {[
                    'Doctor of Medicine (MD) - University of Toronto, 2008',
                    'Family Medicine Residency - St. Michael\'s Hospital, 2010',
                    'Board Certification - College of Family Physicians of Canada (CCFP)',
                    'Advanced Training in Chronic Disease Management',
                    'Certificate in Preventive Medicine',
                    'Member, Ontario Medical Association',
                    'Member, Canadian Medical Association'
                  ].map((credential, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                      <span className="text-gray-700">{credential}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">My Approach to Patient Care</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  I believe that effective healthcare is built on a foundation of trust, communication, and partnership between doctor and patient. My approach emphasizes:
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {[
                    {
                      icon: 'ri-user-heart-line',
                      title: 'Patient-Centered Care',
                      description: 'Your health goals, concerns, and preferences guide every treatment decision we make together.'
                    },
                    {
                      icon: 'ri-microscope-line',
                      title: 'Evidence-Based Medicine',
                      description: 'Treatment plans based on the latest medical research and proven clinical outcomes.'
                    },
                    {
                      icon: 'ri-heart-line',
                      title: 'Holistic Approach',
                      description: 'Addressing physical, mental, and emotional aspects of health for complete wellness.'
                    },
                    {
                      icon: 'ri-time-line',
                      title: 'Preventive Focus',
                      description: 'Proactive strategies to prevent illness and maintain optimal health throughout life.'
                    }
                  ].map((approach, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-xl p-6">
                      <div className="w-14 h-14 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                        <i className={`${approach.icon} text-2xl text-blue-600`}></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">{approach.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{approach.description}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">Areas of Expertise</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {[
                    'Chronic Disease Management (Diabetes, Hypertension)',
                    'Preventive Medicine & Health Screening',
                    'Women\'s Health & Reproductive Care',
                    'Pediatric & Adolescent Medicine',
                    'Geriatric Care & Age-Related Conditions',
                    'Mental Health & Wellness Counseling',
                    'Sports Medicine & Injury Prevention',
                    'Travel Medicine & Vaccination'
                  ].map((expertise, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                      <i className="ri-stethoscope-line text-blue-600 text-lg flex-shrink-0 mt-1"></i>
                      <span className="text-gray-700 text-sm">{expertise}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">Languages Spoken</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  I am fluent in English, French, and Spanish, allowing me to communicate effectively with a diverse patient population and ensure that language is never a barrier to quality healthcare.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">Community Involvement</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Beyond my clinical practice, I am actively involved in community health initiatives, including free health screening programs, health education workshops, and volunteer medical services for underserved populations. I believe in giving back to the community and making healthcare accessible to all.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Our Clinic
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience healthcare that puts you first, with comprehensive services and personalized attention
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-time-line',
                title: 'Flexible Scheduling',
                description: 'Same-day appointments available with extended hours to accommodate your busy schedule.'
              },
              {
                icon: 'ri-video-line',
                title: 'Telehealth Services',
                description: 'Virtual consultations available for your convenience and safety when in-person visits aren\'t necessary.'
              },
              {
                icon: 'ri-hospital-line',
                title: 'Modern Facilities',
                description: 'State-of-the-art medical equipment and comfortable, welcoming clinic environment.'
              },
              {
                icon: 'ri-team-line',
                title: 'Comprehensive Care',
                description: 'Full range of family medicine services from preventive care to chronic disease management.'
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Personalized Treatment',
                description: 'Individualized care plans tailored to your unique health needs and lifestyle.'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Excellent Communication',
                description: 'Always available to answer questions and provide guidance between appointments.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-6">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { number: '15+', label: 'Years of Experience', sublabel: 'Serving the Community' },
              { number: '5000+', label: 'Patients Served', sublabel: 'And Growing' },
              { number: '98%', label: 'Patient Satisfaction', sublabel: 'Based on Reviews' },
              { number: '3', label: 'Languages Spoken', sublabel: 'English, French, Spanish' }
            ].map((stat, index) => (
              <div key={index} className="p-6">
                <div className="text-5xl font-bold mb-3 text-emerald-400">{stat.number}</div>
                <div className="text-xl font-semibold mb-2">{stat.label}</div>
                <div className="text-sm text-blue-200">{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl font-bold text-gray-900 mb-6">
            Ready to Experience Quality Healthcare?
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Whether you're a new patient or looking for a new family doctor, I'm here to provide the compassionate, comprehensive care you deserve.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/#booking-section"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book an Appointment
            </a>
            <a 
              href="tel:+14165551234"
              className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Call Us Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
